// Turns a #rrggbb into an rgba() string — used for the event pills' tinted
// backgrounds, which keep each event type's colour identity without the
// saturated full-colour chips (and unreadable white-on-amber text) the grid
// used before.
const withAlpha = (hex, alpha) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
    return `rgba(46,124,186,${alpha})`;
  }
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt(full, 16);
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
};

const getResponsiveStyles = (screenSize, theme, language) => {
  const { isMobile, isTablet } = screenSize;
  const isRTL = language === "ar";
  const isDark = theme === "dark";

  // ── Calendar surface tokens ───────────────────────────────────────────────
  // The month grid is drawn as ONE table: a single rounded frame with 1px
  // hairlines between the cells (gap over a line-coloured background), instead
  // of 42 separately rounded, shadowed, floating tiles.
  const accent = "#2e7cba";
  const gridLine = isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)";
  const daySurface = isDark ? "rgba(255,255,255,0.02)" : "#ffffff";
  const dayOutside = isDark ? "rgba(255,255,255,0.008)" : "rgba(15,23,42,0.025)";
  const dayWeekend = isDark ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.018)";
  const textPrimary = isDark ? "#f1f5f9" : "#0f172a";
  const textMuted = isDark ? "rgba(226,232,240,0.55)" : "rgba(15,23,42,0.5)";

  return {
    // Exposed so components can build colour-matched accents (event pills,
    // legend dots, the upcoming-events markers) from the same palette.
    tokens: {
      accent,
      gridLine,
      daySurface,
      textPrimary,
      textMuted,
      withAlpha,
    },

    container: {
      padding: isMobile ? "12px" : isTablet ? "16px" : "24px",
      maxWidth: "100%",
      margin: "0 auto",
      minHeight: "100vh",
      // Matches (Admin)EmployeeActions' exact container gradient — the extra
      // third gradient stop this page used to have (#334155 dark / #cbd5e1
      // light) made it read as noticeably more "navy" than the rest of the
      // system; every other page uses this same 2-stop gradient.
      background:
        theme === "dark"
          ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      position: "relative",
      boxSizing: "border-box",
      direction: isRTL ? "rtl" : "ltr",
    },

    // Matches (Admin) Level_Configuration's WelcomeHeader style exactly — a
    // light, almost-invisible-background banner (near-zero-opacity gradient
    // in dark mode, soft slate border, no heavy shadow) instead of a fully
    // opaque dark card with a big drop shadow, which is what made this
    // page's header read as a solid navy block against the reference style.
    headerSection: {
      padding: isMobile ? "18px 16px" : isTablet ? "22px 24px" : "26px 28px",
      borderRadius: isMobile ? "16px" : "24px",
      marginBottom: isMobile ? "16px" : "20px",
      marginTop: -40,
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0) 0%, rgba(15, 23, 42, 0) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
      border:
        theme === "dark"
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.3s ease",
    },

    // ── Header summary strip ─────────────────────────────────────────────────
    // Replaces the five full-size stat cards that used to sit in their own
    // band above the calendar. On a page whose subject is the grid, those
    // numbers are context, not headline — they belong on one line.
    statStrip: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: isMobile ? "8px 10px" : "8px 12px",
      marginTop: isMobile ? "14px" : "16px",
      paddingTop: isMobile ? "14px" : "16px",
      borderTop: `1px solid ${gridLine}`,
    },

    statChip: (color) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "7px",
      padding: isMobile ? "5px 10px" : "6px 12px",
      borderRadius: "999px",
      background: withAlpha(color, isDark ? 0.18 : 0.09),
      border: `1px solid ${withAlpha(color, isDark ? 0.3 : 0.18)}`,
      fontSize: isMobile ? "12px" : "12.5px",
      fontWeight: "600",
      color: textMuted,
      whiteSpace: "nowrap",
    }),

    statChipValue: {
      fontSize: isMobile ? "13.5px" : "14.5px",
      fontWeight: "800",
      color: textPrimary,
      fontVariantNumeric: "tabular-nums",
    },

    // Square icon-only button for the secondary header actions (image / PDF /
    // share). Three equal-weight text buttons used to compete with the one
    // action that matters, "New event".
    iconButton: {
      width: isMobile ? "38px" : "42px",
      height: isMobile ? "38px" : "42px",
      borderRadius: "12px",
      border: `1px solid ${gridLine}`,
      background: isDark ? "rgba(255,255,255,0.06)" : "#ffffff",
      color: textPrimary,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "background 0.2s ease, border-color 0.2s ease",
      padding: 0,
      flexShrink: 0,
    },

    headerContent: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "flex-start" : "center",
      gap: isMobile ? "20px" : "32px",
      position: "relative",
      zIndex: 1,
    },

    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "16px" : "32px",
      flex: 1,
    },

    headerTitle: {
      fontSize: isMobile ? "26px" : isTablet ? "30px" : "34px",
      fontWeight: "800",
      margin: 0,
      color: textPrimary,
      letterSpacing: "-0.02em",
      lineHeight: "1.15",
      textAlign: "start",
      // Was pushed 20px down while sitting in a row next to the action
      // buttons, which knocked the whole header off its baseline.
      marginBottom: isMobile ? 12 : 0,
      whiteSpace: "nowrap",
    },

    headerText: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "flex-start" : "center",
      gap: isMobile ? "12px" : "18px",
      width: "100%",
    },

    headerSubtitle: {
      fontSize: isMobile ? "14px" : isTablet ? "16px" : "18px",
      color:
        theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(26,26,26,0.7)",
      margin: 0,
      lineHeight: "1.6",
      maxWidth: isMobile ? "100%" : "500px",
      textAlign: isRTL ? "right" : "left",
    },

    headerActions: {
      display: "flex",
      gap: isMobile ? "8px" : "16px",
      alignItems: "center",
      flexWrap: "wrap",
      justifyContent: isMobile ? "center" : "flex-end",
      width: isMobile ? "100%" : "auto",
      flexDirection: isRTL ? "row" : "row-reverse",
    },

    // Stat card styles removed — the Calendar's own numbers now render as the
    // compact `statStrip` chips inside the page header above. The Tasks panel
    // still uses the shared src/components/StatsGrid.jsx cards.

    // Same WelcomeHeader-style treatment as headerSection above — used by
    // the "Tasks" panel (and reused by "Upcoming Events" via sidebarCard
    // below), so the whole page reads as one consistent light/transparent
    // look instead of the previous opaque dark cards with heavy shadows.
    controlPanel: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0) 0%, rgba(15, 23, 42, 0) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
      padding: isMobile ? "16px" : isTablet ? "24px" : "32px",
      borderRadius: isMobile ? "16px" : "24px",
      marginBottom: isMobile ? "16px" : isTablet ? "24px" : "32px",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
    },

    controlRow: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? "12px" : "20px",
      alignItems: isMobile ? "stretch" : "center",
      flexWrap: "wrap",
      marginBottom: isMobile ? "16px" : "24px",
    },

    searchContainer: {
      position: "relative",
      flex: isMobile ? "none" : 1,
      minWidth: isMobile ? "100%" : "320px",
      width: isMobile ? "100%" : "auto",
    },

    searchInput: {
      width: "100%",
      padding: isMobile ? "12px 16px 12px 48px" : "16px 20px 16px 56px",
      paddingLeft: isRTL ? "16px" : isMobile ? "48px" : "56px",
      paddingRight: isRTL ? (isMobile ? "48px" : "56px") : "16px",
      borderRadius: isMobile ? "12px" : "16px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.12)"
          : "2px solid rgba(0,0,0,0.08)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "500",
      outline: "none",
      transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      backdropFilter: "blur(10px)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
    },

    calendarContainer: {
      display: "grid",
      gridTemplateColumns: "1fr",
      width: "100%",
      gap: isMobile ? "16px" : "20px",
      alignItems: "start",
      marginBottom: isMobile ? "24px" : "32px",
    },

    // Same WelcomeHeader-style treatment as headerSection / controlPanel /
    // sidebarCard — the calendar grid's own wrapper card.
    calendarMain: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0) 0%, rgba(15, 23, 42, 0) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
      padding: isMobile ? "14px" : isTablet ? "18px" : "22px",
      borderRadius: isMobile ? "16px" : "20px",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxSizing: "border-box",
    },

    // Month bar: title on one side, controls on the other — no full-width
    // divider block, just a light hairline that ties into the grid below.
    calendarHeader: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: isMobile ? "12px" : "16px",
      marginBottom: isMobile ? "12px" : "16px",
      paddingBottom: isMobile ? "12px" : "16px",
      borderBottom: `1px solid ${gridLine}`,
    },

    calendarNavigation: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "8px" : "10px",
      width: isMobile ? "100%" : "auto",
      justifyContent: isMobile ? "space-between" : "flex-start",
    },

    navButton: {
      width: isMobile ? "34px" : "36px",
      height: isMobile ? "34px" : "36px",
      borderRadius: "10px",
      border: `1px solid ${gridLine}`,
      background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
      color: textPrimary,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "background 0.2s ease, border-color 0.2s ease",
      padding: 0,
      flexShrink: 0,
    },

    // "Today" — a quiet ghost button next to the arrows.
    todayButton: {
      height: isMobile ? "34px" : "36px",
      padding: isMobile ? "0 12px" : "0 14px",
      borderRadius: "10px",
      border: `1px solid ${gridLine}`,
      background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
      color: textPrimary,
      fontSize: isMobile ? "12px" : "13px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      transition: "background 0.2s ease, border-color 0.2s ease",
      whiteSpace: "nowrap",
    },

    currentMonth: {
      fontSize: isMobile ? "18px" : isTablet ? "20px" : "22px",
      fontWeight: "800",
      color: textPrimary,
      letterSpacing: "-0.01em",
      display: "flex",
      alignItems: "baseline",
      gap: "8px",
      margin: 0,
    },

    currentYear: {
      fontSize: isMobile ? "14px" : "16px",
      fontWeight: "600",
      color: textMuted,
      letterSpacing: 0,
    },

    // Legend under the grid — only the event types actually present in the
    // visible month, so the colours in the cells are decodable.
    calendarLegend: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: isMobile ? "8px 14px" : "10px 18px",
      paddingTop: isMobile ? "12px" : "14px",
      marginTop: "2px",
      borderTop: `1px solid ${gridLine}`,
    },

    calendarLegendItem: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: isMobile ? "11px" : "12px",
      fontWeight: "600",
      color: textMuted,
      whiteSpace: "nowrap",
    },

    viewModeButtons: {
      display: "flex",
      gap: isMobile ? "4px" : "8px",
      background:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
      borderRadius: isMobile ? "12px" : "16px",
      padding: isMobile ? "4px" : "6px",
      backdropFilter: "blur(10px)",
      order: isMobile ? 2 : 0,
    },

    viewModeButton: (isActive) => ({
      padding: isMobile ? "8px 12px" : "12px 20px",
      borderRadius: isMobile ? "8px" : "12px",
      border: "none",
      background: isActive
        ? "linear-gradient(135deg, #2e7cba, #2e7cba)"
        : "transparent",
      color: isActive
        ? "#fff"
        : theme === "dark"
        ? "rgba(255,255,255,0.7)"
        : "rgba(26,26,26,0.7)",
      fontSize: isMobile ? "12px" : "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
    }),

    // One framed table: 1px gaps over a line-coloured background draw the
    // hairlines, the frame rounds the outside only.
    calendarGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
      gap: "1px",
      background: gridLine,
      border: `1px solid ${gridLine}`,
      borderRadius: isMobile ? "12px" : "16px",
      overflow: "hidden",
      width: "100%",
    },

    calendarDayHeader: {
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
      padding: isMobile ? "9px 2px" : "11px 8px",
      textAlign: "center",
      fontSize: isMobile ? "11px" : "12px",
      fontWeight: "700",
      letterSpacing: isRTL ? "0" : "0.06em",
      textTransform: isRTL ? "none" : "uppercase",
      color: textMuted,
    },

    calendarDay: (dayObj, isSelected, isToday, isVacation, isInRange, getEventsForDate, dateRangeMode) => {
      const { date, isCurrentMonth } = dayObj;
      // Friday / Saturday get a whisper of tint so the week reads at a glance.
      const isWeekend = date && (date.getDay() === 5 || date.getDay() === 6);

      const background = isSelected
        ? withAlpha(accent, isDark ? 0.22 : 0.1)
        : isToday
        ? withAlpha(accent, isDark ? 0.14 : 0.06)
        : isVacation
        ? withAlpha("#06b6d4", isDark ? 0.18 : 0.1)
        : isInRange
        ? withAlpha(accent, isDark ? 0.12 : 0.06)
        : !isCurrentMonth
        ? dayOutside
        : isWeekend
        ? dayWeekend
        : daySurface;

      return {
        background,
        minHeight: isMobile ? "68px" : isTablet ? "92px" : "112px",
        padding: isMobile ? "5px 4px" : "8px 8px",
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "2px" : "3px",
        position: "relative",
        cursor: "pointer",
        transition: "background 0.18s ease, box-shadow 0.18s ease",
        overflow: "hidden",
        boxShadow: isSelected
          ? `inset 0 0 0 2px ${accent}`
          : dateRangeMode
          ? `inset 0 0 0 1px ${withAlpha(accent, 0.45)}`
          : "none",
      };
    },

    // Plain number, except today — a filled accent disc, the single strongest
    // mark on the grid (nothing else fills a whole cell any more).
    calendarDayNumber: (isSelected, isToday, isVacation, hasEvents, isCurrentMonth = true) => ({
      width: isMobile ? "22px" : "26px",
      height: isMobile ? "22px" : "26px",
      borderRadius: "999px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "flex-start",
      flexShrink: 0,
      fontSize: isMobile ? "12px" : "13px",
      fontWeight: isToday || isSelected ? "800" : "600",
      lineHeight: 1,
      background: isToday ? accent : "transparent",
      color: isToday
        ? "#ffffff"
        : !isCurrentMonth
        ? textMuted
        : isSelected
        ? accent
        : textPrimary,
      opacity: !isCurrentMonth && !isToday ? 0.7 : 1,
      letterSpacing: "-0.01em",
    }),

    // Tinted chip + a solid colour bar on the leading edge: the event type
    // stays readable at 11px in both themes (white-on-amber never was).
    eventItem: (event) => ({
      background: withAlpha(event.color, isDark ? 0.24 : 0.12),
      borderInlineStart: `3px solid ${event.color}`,
      color: textPrimary,
      padding: isMobile ? "2px 5px" : "3px 7px",
      borderRadius: "5px",
      fontSize: isMobile ? "10px" : "11.5px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "background 0.18s ease",
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "4px" : "6px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      width: "100%",
      textAlign: "start",
      border: "none",
      borderInlineEnd: "none",
      borderBlock: "none",
      minHeight: isMobile ? "16px" : "20px",
    }),

    eventItemTime: {
      fontSize: isMobile ? "9px" : "10.5px",
      fontWeight: "700",
      color: textMuted,
      flexShrink: 0,
      fontVariantNumeric: "tabular-nums",
    },

    moreEventsButton: {
      background: "none",
      border: "none",
      padding: isMobile ? "1px 4px" : "2px 6px",
      marginTop: "1px",
      fontSize: isMobile ? "9px" : "10.5px",
      fontWeight: "700",
      color: accent,
      cursor: "pointer",
      textAlign: "start",
      width: "100%",
      borderRadius: "4px",
    },

    sidebar: {
      display: "flex",
      flexDirection: "column",
      gap: isMobile ? "16px" : "24px",
      order: isMobile ? -1 : 0,
    },

    // "Upcoming Events" — same WelcomeHeader-style treatment as headerSection
    // / controlPanel above.
    sidebarCard: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0) 0%, rgba(15, 23, 42, 0) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
      padding: isMobile ? "16px" : "20px",
      borderRadius: isMobile ? "16px" : "20px",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border:
        theme === "dark"
          ? "1px solid rgba(148, 163, 184, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      transition: "all 0.3s ease",
      // Hugs its content and sticks while the calendar scrolls, instead of
      // stretching to the calendar's full height around an empty state.
      alignSelf: "start",
      position: isMobile ? "static" : "sticky",
      top: isMobile ? undefined : "16px",
      maxHeight: isMobile ? "none" : "calc(100vh - 32px)",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
      boxSizing: "border-box",
    },

    // ── Upcoming events list ────────────────────────────────────────────────
    upcomingItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: isMobile ? "10px" : "12px",
      padding: isMobile ? "10px" : "12px",
      borderRadius: "12px",
      border: `1px solid ${gridLine}`,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(248,250,252,0.8)",
      marginBottom: "8px",
      cursor: "pointer",
      transition: "background 0.2s ease, border-color 0.2s ease",
      textAlign: "start",
      width: "100%",
    },

    upcomingDateChip: (color) => ({
      width: isMobile ? "40px" : "44px",
      flexShrink: 0,
      borderRadius: "10px",
      background: withAlpha(color || accent, isDark ? 0.22 : 0.12),
      color: isDark ? "#f1f5f9" : color || accent,
      padding: "6px 0",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1.1,
    }),

    upcomingDateDay: {
      fontSize: isMobile ? "16px" : "18px",
      fontWeight: "800",
      fontVariantNumeric: "tabular-nums",
    },

    upcomingDateMonth: {
      fontSize: "10px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      opacity: 0.85,
    },

    upcomingTitle: {
      fontSize: isMobile ? "13.5px" : "14px",
      fontWeight: "700",
      color: textPrimary,
      lineHeight: 1.35,
      marginBottom: "4px",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },

    upcomingMeta: {
      fontSize: isMobile ? "11.5px" : "12px",
      color: textMuted,
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "5px",
      marginTop: "2px",
      minWidth: 0,
    },

    upcomingUrgentTag: {
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      marginTop: "6px",
      padding: "2px 8px",
      borderRadius: "999px",
      background: withAlpha("#ef4444", isDark ? 0.24 : 0.12),
      color: isDark ? "#fca5a5" : "#dc2626",
      fontSize: "11px",
      fontWeight: "700",
    },

    sectionCount: {
      marginInlineStart: "auto",
      padding: "2px 10px",
      borderRadius: "999px",
      background: withAlpha(accent, isDark ? 0.26 : 0.12),
      color: isDark ? "#bfdbfe" : accent,
      fontSize: "12px",
      fontWeight: "700",
      fontVariantNumeric: "tabular-nums",
    },

    sidebarCard1: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.98)",
      padding: isMobile ? "20px 16px" : "28px",
      borderRadius: isMobile ? "16px" : "20px",
      backdropFilter: "blur(25px)",
      WebkitBackdropFilter: "blur(25px)",
      maxWidth: "100px",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      marginBottom: 33,
      boxShadow:
        theme === "dark"
          ? "0 20px 40px rgba(0,0,0,0.4)"
          : "0 20px 40px rgba(0,0,0,0.12)",
      transition: "all 0.3s ease",
    },

    sidebarTitle: {
      fontSize: isMobile ? "15px" : "17px",
      fontWeight: "700",
      color: textPrimary,
      margin: `0 0 ${isMobile ? "12px" : "16px"} 0`,
      display: "flex",
      alignItems: "center",
      gap: "10px",
      // The document's own dir already mirrors this row — forcing
      // row-reverse on top of it flipped it back the wrong way.
      textAlign: "start",
    },

    actionButton: (variant = "default", size = "medium") => {
      const variants = {
        default: {
          background: isDark ? "rgba(255,255,255,0.06)" : "#ffffff",
          color: textPrimary,
          border: `1px solid ${gridLine}`,
        },
        primary: {
          background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
          color: "#fff",
          border: "none",
        },
        success: {
          background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
          color: "#fff",
          border: "none",
        },
        warning: {
          background: "linear-gradient(135deg, #f59e0b, #d97706)",
          color: "#fff",
          border: "none",
          boxShadow: "0 8px 25px rgba(245, 158, 11, 0.4)",
        },
        danger: {
          background: "linear-gradient(135deg, #ef4444, #dc2626)",
          color: "#fff",
          border: "none",
          boxShadow: "0 8px 25px rgba(239, 68, 68, 0.4)",
        },
      };

      const sizes = {
        small: {
          padding: isMobile ? "8px 12px" : "10px 16px",
          fontSize: isMobile ? "12px" : "13px",
        },
        medium: {
          padding: isMobile ? "10px 16px" : "14px 24px",
          fontSize: isMobile ? "13px" : "14px",
        },
        large: {
          padding: isMobile ? "14px 20px" : "18px 32px",
          fontSize: isMobile ? "14px" : "16px",
        },
      };

      return {
        ...variants[variant],
        ...sizes[size],
        borderRadius: isMobile ? "10px" : "12px",
        fontWeight: "700",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: isMobile ? "8px" : "10px",
        outline: "none",
        // Was uppercase + tracked out: it shouted in English and did nothing
        // for Arabic, which has no case.
        letterSpacing: "0",
        backdropFilter: "blur(15px)",
        position: "relative",
        overflow: "hidden",
        whiteSpace: "nowrap",
        flexDirection: isRTL ? "row-reverse" : "row",
      };
    },

    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      // Matches (Admin)EmployeeActions' exact overlay (0.75 opacity, 16px
      // blur) — this page's overlay was darker/blurrier (0.8, 12px... in the
      // wrong direction) than the system standard.
      background: "rgba(0, 0, 0, 0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(16px)",
      padding: isMobile ? "10px" : "20px",
    },

    modalContainer: {
      // Matches (Admin)EmployeeActions' modalContent gradient exactly — the
      // flat rgba(15,23,42,0.95) this used before was noticeably darker
      // ("black") than the system's lighter slate gradient at 0.99 opacity.
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0.99), rgba(51, 65, 85, 0.99))"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 252, 0.99))",
      backdropFilter: "blur(30px)",
      WebkitBackdropFilter: "blur(30px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.08)",
      borderRadius: isMobile ? "16px" : "24px",
      padding: "0",
      width: "100%",
      maxWidth: isMobile ? "95vw" : "700px",
      maxHeight: isMobile ? "95vh" : "90vh",
      overflow: "hidden",
      boxShadow: "0 32px 64px -12px rgba(0,0,0,0.6)",
      direction: isRTL ? "rtl" : "ltr",
    },

    modalHeader: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "flex-start" : "center",
      gap: isMobile ? "16px" : "20px",
      padding: isMobile ? "20px 20px 0 20px" : "32px 32px 0 32px",
      marginBottom: isMobile ? "16px" : "24px",
    },

    modalTitleSection: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "16px" : "20px",
      flex: 1,
      flexDirection: isRTL ? "row" : "row",
    },

    modalIcon: {
      width: isMobile ? "48px" : "56px",
      height: isMobile ? "48px" : "56px",
      borderRadius: isMobile ? "12px" : "16px",
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      flexShrink: 0,
    },

    modalTitle: {
      fontSize: isMobile ? "20px" : "28px",
      fontWeight: "800",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      margin: 0,
      letterSpacing: "-0.01em",
      textAlign: isRTL ? "right" : "left",
    },

    modalSubtitle: {
      fontSize: isMobile ? "13px" : "15px",
      color:
        theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.6)",
      margin: "4px 0 0 0",
      fontWeight: "500",
      textAlign: isRTL ? "right" : "left",
    },

    closeButton: {
      background: "none",
      border: "none",
      color:
        theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)",
      cursor: "pointer",
      padding: isMobile ? "8px" : "12px",
      borderRadius: isMobile ? "8px" : "12px",
      transition: "all 0.2s ease",
      fontSize: isMobile ? "20px" : "24px",
      fontWeight: "300",
      flexShrink: 0,
    },

    formContainer: {
      padding: isMobile ? "0 16px 16px" : "0 32px 32px",
      maxHeight: isMobile ? "calc(95vh - 120px)" : "calc(90vh - 120px)",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
    },

    formGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
      gap: isMobile ? "12px" : "12px",
      alignItems: "start",
      columnGap: isMobile ? "12px" : "16px",
      marginBottom: isMobile ? "12px" : "16px",
    },

    formGroup: {
      marginBottom: isMobile ? "16px" : "20px",
    },

    formLabel: {
      display: "block",
      fontSize: isMobile ? "13px" : "14px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      marginBottom: "8px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      textAlign: isRTL ? "right" : "left",
    },

    formInput: {
      width: "100%",
      padding: isMobile ? "8px 12px" : "10px 14px",
      borderRadius: isMobile ? "10px" : "14px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.12)"
          : "2px solid rgba(0,0,0,0.08)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "500",
      outline: "none",
      transition: "all 0.3s ease",
      backdropFilter: "blur(10px)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
      minHeight: "40px",
      display: "flex",
      alignItems: "center",
    },

    formInputWrapper: {
      width: "100%",
      minHeight: "40px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },

    formTextarea: {
      width: "100%",
      padding: isMobile ? "12px 16px" : "16px 20px",
      borderRadius: isMobile ? "10px" : "14px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.12)"
          : "2px solid rgba(0,0,0,0.08)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "500",
      outline: "none",
      resize: "vertical",
      minHeight: isMobile ? "80px" : "120px",
      fontFamily: "inherit",
      transition: "all 0.3s ease",
      backdropFilter: "blur(10px)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
    },

    formSelect: {
      width: "100%",
      padding: isMobile ? "8px 12px" : "10px 14px",
      borderRadius: isMobile ? "10px" : "14px",
      border:
        theme === "dark"
          ? "2px solid rgba(255,255,255,0.12)"
          : "2px solid rgba(0,0,0,0.08)",
      background:
        theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "500",
      outline: "none",
      cursor: "pointer",
      transition: "all 0.3s ease",
      backdropFilter: "blur(10px)",
      boxSizing: "border-box",
      textAlign: isRTL ? "right" : "left",
    },

    formActions: {
      display: "flex",
      flexDirection: isMobile ? "column" : isRTL ? "row-reverse" : "row",
      gap: isMobile ? "12px" : "16px",
      justifyContent: "flex-end",
      paddingTop: isMobile ? "16px" : "24px",
      borderTop:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.08)",
    },

    loadingOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: isMobile ? "16px" : "24px",
      backdropFilter: "blur(8px)",
      zIndex: 10,
    },

    loadingSpinner: {
      width: isMobile ? "32px" : "48px",
      height: isMobile ? "32px" : "48px",
      border: "4px solid rgba(255,255,255,0.2)",
      borderTop: "4px solid #2e7cba",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },

    emptyState: {
      padding: isMobile ? "40px 16px" : "80px 32px",
      textAlign: "center",
      color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280",
    },

    // Same idea, sized for a sidebar panel rather than a full page section —
    // the 80px-tall version left the "Upcoming events" card mostly air.
    emptyStateCompact: {
      padding: isMobile ? "24px 12px" : "32px 16px",
      textAlign: "center",
      color: textMuted,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "6px",
    },

    emptyIconCompact: {
      width: isMobile ? "36px" : "40px",
      height: isMobile ? "36px" : "40px",
      marginBottom: "4px",
      opacity: 0.45,
      color: textMuted,
    },

    emptyTitleCompact: {
      fontSize: isMobile ? "14px" : "15px",
      fontWeight: "700",
      color: textPrimary,
      margin: 0,
    },

    emptyIcon: {
      width: isMobile ? "64px" : "96px",
      height: isMobile ? "64px" : "96px",
      margin: "0 auto 16px",
      opacity: 0.6,
      color: theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
    },

    emptyTitle: {
      fontSize: isMobile ? "18px" : "24px",
      fontWeight: "700",
      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "#374151",
      margin: "0 0 12px 0",
    },

    emptyDescription: {
      fontSize: isMobile ? "14px" : "16px",
      opacity: 0.8,
      margin: 0,
      lineHeight: "1.5",
    },

    emptyDescription1: {
      fontSize: isMobile ? "14px" : "16px",
      opacity: 0.8,
      margin: 0,
      marginBottom: "32px",
      lineHeight: "1.5",
    },
  };
};

export default getResponsiveStyles;
